import { Box, Divider, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { AgreementTermType, License, State, StateExhibitTemplate } from '@uplift/api';
import { useSignStateExhibitMutation, useUserDisplayNameQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { Button, CheckboxWithLabel, Markdown, SubmitButton, useAlert } from '@uplift/ui-v2';

const useStyles = makeStyles()(theme => ({
  root: {
    maxWidth: 700,
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(6, 0),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    marginBottom: theme.spacing(4),
  },
  overline: {
    display: 'block',
    color: theme.palette.primary.main,
    fontWeight: 700,
    letterSpacing: 1,
  },
  summary: {
    marginBottom: theme.spacing(4),
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
  },
  exhibitCard: {
    padding: theme.spacing(3),
  },
  exhibitHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  meta: {
    display: 'flex',
    gap: theme.spacing(5),
    marginBottom: theme.spacing(2.5),
  },
  content: {
    maxHeight: 360,
    overflowY: 'auto',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

export type ExhibitSignatureStepProps = {
  providerId: string;
  license: Pick<License, 'id' | 'userId' | 'stateId'>;
  state?: State;
  exhibitTemplate: StateExhibitTemplate;
  onBack: () => void;
  onComplete: (expireAt: string) => void;
};

const exhibitSignatureValidationSchema = Yup.object().shape({
  agree: Yup.boolean().oneOf([ true ], 'You must agree to the electronic signature terms'),
});

const buildRenewalLabel = (value: number, unit: AgreementTermType) => {
  const singularUnit = unit.replace(/s$/, '');

  return value === 1 ? `Every ${singularUnit}` : `Every ${value} ${unit}`;
};

const MetaItem = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography fontWeight={600}>{value}</Typography>
  </Box>
);

export const ExhibitSignatureStep = ({ providerId, license, state, exhibitTemplate, onBack, onComplete }: ExhibitSignatureStepProps) => {
  const { classes } = useStyles();
  const alert = useAlert();
  const { data: providerName } = useUserDisplayNameQuery(providerId, { suspense: true });
  const { template, version } = exhibitTemplate;
  const signMutation = useSignStateExhibitMutation(providerId, license.id);

  const expiresLabel = CalendarDateService.date()
    .add(template.agreementTermValue, template.agreementTermType)
    .format(DateFormats['MMMM D, YYYY']);
  const renewsLabel = buildRenewalLabel(template.agreementTermValue, template.agreementTermType);

  const onSubmit = async () => {
    try {
      const signed = await signMutation.mutateAsync({ templateId: template.id, version: version.version });

      onComplete(signed.data.expireAt);
    } catch (err) {
      alert.error(err.message);
    }
  };

  return (
    <Formik
      initialValues={{ agree: false }}
      validationSchema={exhibitSignatureValidationSchema}
      onSubmit={onSubmit}
    >
      {formik => (
        <Box className={classes.root}>
          <Typography sx={{ fontSize: '24px', fontWeight: 600 }} className={classes.title}>
            Your {state?.fullName} setup
          </Typography>
          <Typography color="text.secondary" className={classes.subtitle}>
            You qualify for independent practice. Sign your state exhibit to go live — no review needed.
          </Typography>

          <Paper variant="outlined" className={classes.summary}>
            <Box className={classes.summaryRow}>
              <Box>
                <Typography variant="body2" color="text.secondary">Practice type</Typography>
                <Typography>Independent practice</Typography>
              </Box>
              <Button variant="text" color="primary" onClick={onBack}>Change</Button>
            </Box>
          </Paper>

          <Typography variant="overline" className={classes.overline} sx={{ mb: 1 }}>
            YOUR STATE EXHIBIT
          </Typography>

          <Paper variant="outlined" className={classes.exhibitCard} sx={{ mb: 4 }}>
            <Box className={classes.exhibitHeader}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{template.name}</Typography>
            </Box>

            <Box className={classes.meta}>
              <MetaItem label="EFFECTIVE" value="Upon signing" />
              <MetaItem label="EXPIRES" value={expiresLabel} />
              <MetaItem label="RENEWS" value={renewsLabel} />
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box className={classes.content}>
              <Markdown>{version.content}</Markdown>
            </Box>
          </Paper>

          <Typography variant="overline" className={classes.overline} sx={{ mb: 1.5 }}>
            YOUR SIGNATURE
          </Typography>

          <Box display="flex" flexDirection="column" gap="14px" sx={{ mb: 4 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography>{providerName.fullLegalName}</Typography>
            </Paper>
            <CheckboxWithLabel
              name="agree"
              showError
              label="By checking this, I agree this constitutes my legal electronic signature on this state exhibit."
            />
          </Box>

          <Box className={classes.actions}>
            <SubmitButton>Sign &amp; go live</SubmitButton>
            <Button variant="outlined" disabled={formik.isSubmitting} onClick={onBack}>
              Back
            </Button>
          </Box>
        </Box>
      )}
    </Formik>
  );
};
